import React from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  MessageCircle,
  Send,
  Share2,
  X,
} from "lucide-react";
import { resolveMediaUrl } from "../utils/mediaUrl";
import {
  canUseNativeShare,
  condividiSuSocial,
  toAbsoluteShareUrl,
} from "../utils/condividiSuSocial";
import { buildPhotoDeepLink, syncFotoQueryInUrl } from "../utils/photoShareUrl";

export default function ImageLightbox({
  images = [],
  initialIndex = 0,
  title = "Galleria immagini",
  backLabel = "Torna alla scheda",
  onClose,
  onIndexChange,
  /** When true, show share actions for the current photo (deep link ?foto=N). */
  enablePhotoShare = true,
  /** Optional base path/URL for deep links; defaults to current location. */
  shareBaseUrl,
}) {
  const gallery = React.useMemo(() => {
    const seen = new Set();
    return images
      .filter(Boolean)
      .map(resolveMediaUrl)
      .filter((src) => {
        if (!src || seen.has(src)) return false;
        seen.add(src);
        return true;
      });
  }, [images]);
  const [active, setActive] = React.useState(() =>
    Math.max(0, Math.floor(Number(initialIndex) || 0))
  );
  const [copied, setCopied] = React.useState(false);
  const closeButtonRef = React.useRef(null);
  const closedByHistoryRef = React.useRef(false);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const safeActive = gallery.length ? Math.min(Math.max(active, 0), gallery.length - 1) : 0;

  const photoShareUrl = React.useMemo(() => {
    const base =
      shareBaseUrl ||
      (typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "");
    return buildPhotoDeepLink(base, safeActive);
  }, [shareBaseUrl, safeActive]);

  const photoAbsUrl = React.useMemo(
    () => toAbsoluteShareUrl(gallery[safeActive] || ""),
    [gallery, safeActive]
  );

  const shareTitle = `${title} · foto ${safeActive + 1}`;
  const shareText = `${shareTitle} — ${photoShareUrl}`;

  const shareLinks = React.useMemo(
    () => ({
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${photoAbsUrl}`)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(photoShareUrl)}&text=${encodeURIComponent(shareTitle)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(photoShareUrl)}`,
      x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(photoShareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    }),
    [photoShareUrl, photoAbsUrl, shareText, shareTitle]
  );

  const requestClose = React.useCallback(() => {
    if (typeof window !== "undefined" && window.history.state?.imageLightbox) {
      window.history.back();
      return;
    }
    onClose?.();
  }, [onClose]);

  const goTo = React.useCallback(
    (index) => {
      if (!gallery.length) return;
      const next = (index + gallery.length) % gallery.length;
      setActive(next);
      onIndexChange?.(next);
      if (enablePhotoShare) syncFotoQueryInUrl(next);
    },
    [gallery.length, onIndexChange, enablePhotoShare]
  );

  React.useEffect(() => {
    const next = gallery.length ? Math.min(Math.max(initialIndex || 0, 0), gallery.length - 1) : 0;
    setActive(next);
  }, [gallery.length, initialIndex]);

  React.useEffect(() => {
    if (!gallery.length) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [gallery.length]);

  React.useEffect(() => {
    if (!gallery.length || typeof window === "undefined") return undefined;

    closedByHistoryRef.current = false;
    const openIndex = gallery.length
      ? Math.min(Math.max(initialIndex || 0, 0), gallery.length - 1)
      : 0;
    if (enablePhotoShare) syncFotoQueryInUrl(openIndex);
    window.history.pushState({ imageLightbox: true }, "");

    const handlePopState = () => {
      closedByHistoryRef.current = true;
      if (enablePhotoShare) syncFotoQueryInUrl(null);
      onCloseRef.current?.();
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (!closedByHistoryRef.current && window.history.state?.imageLightbox) {
        window.history.back();
      }
    };
    // Only remount history when gallery opens (length), not on every photo change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gallery.length, enablePhotoShare]);

  React.useEffect(() => {
    if (!gallery.length) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") requestClose();
      if (event.key === "ArrowLeft" && gallery.length > 1) goTo(safeActive - 1);
      if (event.key === "ArrowRight" && gallery.length > 1) goTo(safeActive + 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gallery.length, goTo, requestClose, safeActive]);

  async function copyPhotoLink() {
    try {
      await navigator.clipboard.writeText(photoShareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt("Copia il link della foto:", photoShareUrl);
    }
  }

  async function nativePhotoShare() {
    await condividiSuSocial(shareTitle, photoShareUrl, {
      text: `${shareTitle}\n${photoAbsUrl}`,
    });
  }

  if (!gallery.length || typeof document === "undefined") return null;

  const shareBtnClass =
    "inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1.5 text-[11px] text-white hover:bg-white/20 transition-colors";

  return createPortal(
    <div
      className="fixed inset-0 z-[950] flex items-center justify-center bg-[#02040a]/95 px-3 py-4 backdrop-blur-sm sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={requestClose}
      data-testid="image-lightbox"
    >
      <div className="relative flex h-full w-full max-w-6xl flex-col" onClick={(event) => event.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between gap-3 text-white">
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border border-[rgba(0,217,255,0.28)] bg-white/10 px-3 py-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-[var(--social-blue)] sm:px-4"
            aria-label={backLabel}
            data-testid="image-lightbox-close"
          >
            <ArrowLeft size={18} aria-hidden />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] sm:text-[11px]">{backLabel}</span>
            <X size={18} className="hidden sm:block" aria-hidden />
          </button>
          <div className="min-w-0 flex-1 text-right sm:text-left">
            <p className="truncate font-display text-lg leading-tight sm:text-2xl">{title}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--social-blue)]">
              {safeActive + 1} / {gallery.length} foto
            </p>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl border border-[rgba(0,217,255,0.22)] bg-black/55 shadow-[0_0_40px_-18px_rgba(0,217,255,0.95)]">
          <img
            src={gallery[safeActive]}
            alt={`${title} ${safeActive + 1}`}
            decoding="async"
            className="max-h-full max-w-full object-contain"
          />
          {gallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => goTo(safeActive - 1)}
                className="absolute left-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/12 text-white shadow-lg backdrop-blur transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-[var(--social-blue)] sm:left-4 sm:h-12 sm:w-12"
                aria-label="Foto precedente"
                data-testid="image-lightbox-prev"
              >
                <ChevronLeft size={24}/>
              </button>
              <button
                type="button"
                onClick={() => goTo(safeActive + 1)}
                className="absolute right-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/12 text-white shadow-lg backdrop-blur transition-colors hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-[var(--social-blue)] sm:right-4 sm:h-12 sm:w-12"
                aria-label="Foto successiva"
                data-testid="image-lightbox-next"
              >
                <ChevronRight size={24}/>
              </button>
            </>
          )}
        </div>

        {enablePhotoShare && (
          <div
            className="mt-3 flex flex-wrap items-center gap-2"
            role="group"
            aria-label="Condividi questa foto"
            data-testid="image-lightbox-share"
          >
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 mr-1">
              <Share2 size={12} aria-hidden /> Condividi questa foto
            </span>
            <a
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className={shareBtnClass}
              data-testid="lightbox-share-whatsapp"
            >
              <MessageCircle size={13} aria-hidden /> WhatsApp
            </a>
            <a
              href={shareLinks.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className={shareBtnClass}
              data-testid="lightbox-share-telegram"
            >
              <Send size={13} aria-hidden /> Telegram
            </a>
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={shareBtnClass}
              data-testid="lightbox-share-facebook"
            >
              <span className="font-display text-[13px] leading-none w-3 text-center" aria-hidden>
                f
              </span>{" "}
              Facebook
            </a>
            <a
              href={shareLinks.x}
              target="_blank"
              rel="noopener noreferrer"
              className={shareBtnClass}
              data-testid="lightbox-share-x"
            >
              <span className="font-display text-[12px] font-bold leading-none" aria-hidden>
                X
              </span>
            </a>
            <button
              type="button"
              onClick={copyPhotoLink}
              className={shareBtnClass}
              data-testid="lightbox-share-copy"
            >
              {copied ? <Check size={13} className="text-sage" aria-hidden /> : <LinkIcon size={13} aria-hidden />}
              {copied ? "Copiato!" : "Copia link"}
            </button>
            {canUseNativeShare() && (
              <button
                type="button"
                onClick={nativePhotoShare}
                className={shareBtnClass}
                data-testid="lightbox-share-native"
              >
                <Share2 size={13} aria-hidden /> Altro
              </button>
            )}
          </div>
        )}

        {gallery.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label={`Miniature ${title}`}>
            {gallery.map((src, index) => (
              <button
                key={`${src}-${index}`}
                type="button"
                onClick={() => goTo(index)}
                className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 bg-black transition-colors sm:h-16 sm:w-24 ${
                  safeActive === index ? "border-[var(--social-gold)]" : "border-transparent hover:border-white/60"
                }`}
                aria-label={`Mostra foto ${index + 1}`}
              >
                <img src={src} alt="" width="96" height="64" className="h-full w-full object-contain" loading="lazy" decoding="async"/>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
